// src/components/lesson/Diagram.tsx
//
// Single dispatcher that picks the right diagram component based on
// `type`. Lessons reference diagrams via `[[diagram:<id>]]` markers in
// markdown; the renderer (LessonClient) resolves the id to a
// LessonDiagram object and passes it here.

import type { LessonDiagram } from '@/lib/study-plan/lesson-generation'
import { diagramDescription } from '@/lib/render/diagram-alt-text'
import { FIGURE_TEXT_ATTR } from '@/lib/render/figure-text'
import FunctionGraph from './diagrams/FunctionGraph'
import NumberLine from './diagrams/NumberLine'
import SentenceDiagram from './diagrams/SentenceDiagram'
import ComparisonTable from './diagrams/ComparisonTable'
import DecisionTree from './diagrams/DecisionTree'
import HighlightedPassage from './diagrams/HighlightedPassage'
import RightTriangle from './diagrams/RightTriangle'
import RightTriangleAltitude from './diagrams/RightTriangleAltitude'
import UnitCircle from './diagrams/UnitCircle'
import RectangularPrism from './diagrams/RectangularPrism'
import Cylinder from './diagrams/Cylinder'
import Circle from './diagrams/Circle'
import Triangle from './diagrams/Triangle'
import ParallelLines from './diagrams/ParallelLines'
import Polygon from './diagrams/Polygon'
import Cone from './diagrams/Cone'
import Sphere from './diagrams/Sphere'
import Pyramid from './diagrams/Pyramid'
import BoxPlot from './diagrams/BoxPlot'
import DotPlot from './diagrams/DotPlot'
import PolygonShape from './diagrams/PolygonShape'

/**
 * A stable id for a figure's description. NOT useId(): the PDF export calls
 * this component as a plain function (src/lib/pdf/stem-visuals.ts), where a
 * hook throws and every figure would print as a placeholder. Derived from the
 * figure's content, so two identical figures on one page share an id — and
 * point at identical text, which is harmless.
 */
function descriptionId(diagram: LessonDiagram): string {
  const src = `${diagram.type}|${diagram.id}|${JSON.stringify(diagram.params ?? {})}`
  let h = 5381
  for (let i = 0; i < src.length; i++) h = ((h << 5) + h + src.charCodeAt(i)) | 0
  return `figure-desc-${(h >>> 0).toString(36)}`
}

export default function Diagram({ diagram }: { diagram: LessonDiagram }) {
  const inner = (() => {
    switch (diagram.type) {
      case 'function-graph':
        return <FunctionGraph params={diagram.params} />
      case 'number-line':
        return <NumberLine params={diagram.params} />
      case 'sentence-diagram':
        return <SentenceDiagram params={diagram.params} />
      case 'comparison-table':
        return <ComparisonTable params={diagram.params} />
      case 'decision-tree':
        return <DecisionTree params={diagram.params} />
      case 'highlighted-passage':
        return <HighlightedPassage params={diagram.params} />
      case 'right-triangle':
        return <RightTriangle params={diagram.params} />
      case 'right-triangle-altitude':
        return <RightTriangleAltitude params={diagram.params} />
      case 'unit-circle':
        return <UnitCircle params={diagram.params} />
      case 'rectangular-prism':
        return <RectangularPrism params={diagram.params} />
      case 'cylinder':
        return <Cylinder params={diagram.params} />
      case 'circle':
        return <Circle params={diagram.params} />
      case 'triangle':
        return <Triangle params={diagram.params} />
      case 'parallel-lines':
        return <ParallelLines params={diagram.params} />
      case 'polygon':
        return <Polygon params={diagram.params} />
      case 'cone':
        return <Cone params={diagram.params} />
      case 'sphere':
        return <Sphere params={diagram.params} />
      case 'pyramid':
        return <Pyramid params={diagram.params} />
      case 'box-plot':
        return <BoxPlot params={diagram.params} />
      case 'dot-plot':
        return <DotPlot params={diagram.params} />
      case 'shape':
        return <PolygonShape params={diagram.params} />
      default:
        return null
    }
  })()
  if (!inner) return null

  // A graphic figure is wrapped in ONE role="img" carrying a short accessible
  // name ("Graph of two lines in the xy-plane"), and its full text equivalent —
  // every label drawn on it, a graph's points, intercepts and intersections —
  // follows as visually hidden text. That text is in the reading order, so a
  // screen reader reaches it right after the figure and BEFORE the answer
  // choices, with or without description announcements turned on; it is also
  // tied to the image with aria-describedby so jumping to the graphic reads it.
  // (An aria-label alone flattened a system of two lines to "Function graph
  // showing a line" — QA could not answer the question from it.)
  //
  // `diagramDescription` returns null for diagrams that are already
  // accessible DOM (data tables, reading passages, decision trees); those
  // render unwrapped so their native semantics reach assistive tech rather
  // than being flattened to an image.
  const desc = diagramDescription(diagram)
  const hasDetails = !!desc && desc.details.length > 0
  const descId = hasDetails ? descriptionId(diagram) : undefined
  const figure = desc ? (
    <>
      <div role="img" aria-label={desc.name} aria-describedby={descId}>
        {/* The renderers carry their own fallback role="img"; this wrapper is the
            one image a screen reader should meet, so the drawing is hidden. */}
        <div aria-hidden="true">{inner}</div>
      </div>
      {hasDetails && (
        // Marked as figure text: passage-highlight offsets skip it (see figure-text.ts).
        <div id={descId} className="sr-only" {...{ [FIGURE_TEXT_ATTR]: '' }}>
          <p>Description of the figure:</p>
          <ul>
            {desc.details.map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        </div>
      )}
    </>
  ) : (
    inner
  )

  return (
    <div className="not-prose">
      {figure}
      {diagram.caption && (
        // break-words + [overflow-wrap:anywhere] so a long caption
        // (e.g. "Two points: (1,3) and (5,11) showing rise = 8,
        // run = 4") wraps cleanly inside narrow chat bubbles rather
        // than running past the edge.
        <p className="-mt-1 mb-3 break-words text-center text-xs italic text-muted-foreground [overflow-wrap:anywhere]">
          {diagram.caption}
        </p>
      )}
    </div>
  )
}
